import React, { useState } from 'react';
import { Plus, Trash2, RotateCcw } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { CARE_ICONS, DEFAULT_CARE_ITEMS } from './CareInstructions';

// Preset icon keys admin can pick from (matches CareInstructions.CARE_ICONS)
const ICON_OPTIONS = [
  { key: 'coldWash', label: 'Cold Wash' },
  { key: 'noBleach', label: 'No Bleach' },
  { key: 'noTumble', label: 'No Tumble Dry' },
  { key: 'ironMedium', label: 'Iron Medium' },
  { key: 'coldMachine', label: 'Cold Machine Wash' },
  { key: 'noIronPrint', label: 'No Iron Print' },
  { key: 'custom', label: 'Custom' },
];

/**
 * value: array of { iconKey, label } | null | undefined
 * When null/undefined/empty → storefront falls back to the six localized defaults.
 */
const genKey = () => (typeof window !== 'undefined' && window.crypto?.randomUUID ? window.crypto.randomUUID() : `k_${Math.random().toString(36).slice(2)}`);
const withKeys = (arr) => (arr || []).map((it) => ({ _k: it._k || genKey(), ...it }));
const stripKeys = (arr) => arr.map(({ _k, ...rest }) => rest);

const CareInstructionsEditor = ({ value, onChange }) => {
  const isOverride = Array.isArray(value) && value.length > 0;
  const [items, setItems] = useState(isOverride ? withKeys(value) : []);

  const commit = (next) => { setItems(next); onChange(next.length > 0 ? stripKeys(next) : null); };

  const addItem = () => commit([...items, { _k: genKey(), iconKey: 'custom', label: '' }]);
  const removeItem = (i) => commit(items.filter((_, idx) => idx !== i));
  const updateItem = (i, patch) => commit(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const startFromDefaults = () => commit(DEFAULT_CARE_ITEMS.map((d) => ({ _k: genKey(), iconKey: d.iconKey, label: d.label || d.tKey.replace('care_', '') })));
  const resetToDefault = () => { setItems([]); onChange(null); };

  return (
    <div className="border rounded-lg p-4 bg-neutral-50" data-testid="care-editor">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">Care Instructions</label>
          <p className="text-[11px] text-gray-500 mt-0.5">
            {isOverride
              ? 'Custom list — these replace the six localized defaults on this product only.'
              : 'Empty — the storefront will show the six localized defaults for this product.'}
          </p>
        </div>
        <div className="flex gap-2">
          {!isOverride ? (
            <Button type="button" variant="outline" size="sm" onClick={startFromDefaults} data-testid="care-start-from-defaults">
              Start from defaults
            </Button>
          ) : (
            <Button type="button" variant="outline" size="sm" onClick={resetToDefault} data-testid="care-reset">
              <RotateCcw size={12} className="mr-1" /> Reset to defaults
            </Button>
          )}
          <Button type="button" size="sm" onClick={addItem} className="bg-[#22c55e] hover:bg-[#16a34a] text-black" data-testid="care-add-item">
            <Plus size={14} className="mr-1" /> Add line
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-6 text-gray-400 text-sm border-2 border-dashed rounded">
          No custom care lines. Defaults will be used.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => {
            const Icon = CARE_ICONS[it.iconKey] || CARE_ICONS.custom;
            return (
              <div key={it._k} className="flex items-center gap-2 bg-white p-2 border rounded" data-testid={`care-line-${i}`}>
                <div className="w-8 h-8 text-gray-700 shrink-0">
                  <Icon />
                </div>
                <select
                  value={it.iconKey}
                  onChange={(e) => updateItem(i, { iconKey: e.target.value })}
                  className="border rounded px-2 py-1 text-xs h-9 shrink-0"
                  data-testid={`care-icon-select-${i}`}
                >
                  {ICON_OPTIONS.map((o) => (
                    <option key={o.key} value={o.key}>{o.label}</option>
                  ))}
                </select>
                <Input
                  value={it.label}
                  onChange={(e) => updateItem(i, { label: e.target.value })}
                  placeholder="e.g. Hand wash only"
                  className="flex-1 h-9"
                  data-testid={`care-label-input-${i}`}
                />
                <button
                  type="button"
                  onClick={() => removeItem(i)}
                  className="p-1.5 rounded hover:bg-red-100 text-red-600 shrink-0"
                  title="Remove"
                  data-testid={`care-remove-${i}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CareInstructionsEditor;
